/* ============================================================
   THREE OH SIX — Shop the Look
   Self-contained <shop-the-look> controller. Deliberately NOT built
   on the theme's DrawerComponent: that moves a custom element to
   <body> on every open, re-firing connected/disconnectedCallback.
   Here the custom element stays put and only the plain lightbox <div>
   is moved to <body> ONCE, so listeners + index state survive.
   ============================================================ */
(function () {
  'use strict';

  class ShopTheLook extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId || '';
      this.grid = this.querySelector('.stl__grid');
      this.thumbs = Array.from(this.querySelectorAll('.stl__thumb'));
      this.moreBtn = this.querySelector('.stl__more');
      this.moreWrap = this.querySelector('.stl__more-wrap');
      this.visible = parseInt(this.dataset.initial, 10) || this.thumbs.length;
      this.step = parseInt(this.dataset.step, 10) || this.visible || 1;
      this.index = 0;
      this.isOpen = false;

      // Move the lightbox to <body> once (plain div → no CE lifecycle re-fire).
      this.box = this.querySelector('.stl__lightbox');
      if (this.box) {
        document
          .querySelectorAll('body > .stl__lightbox[data-section-id="' + this.sectionId + '"]')
          .forEach((n) => { if (n !== this.box) n.remove(); });          // clear stale render
        if (this.box.parentElement !== document.body) document.body.appendChild(this.box);
        this.slides = Array.from(this.box.querySelectorAll('.stl__slide'));
        this.dialog = this.box.querySelector('.stl__dialog');
        this.moreThumbs = Array.from(this.box.querySelectorAll('.stl__more-thumb'));
      } else {
        this.slides = [];
        this.moreThumbs = [];
      }

      this.ac = new AbortController();
      const sig = { signal: this.ac.signal };

      this.thumbs.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          // Let the video play/pause circle (.gv-toggle) handle its own clicks.
          if (e.target.closest('.gv-toggle')) return;
          this.open(parseInt(btn.dataset.index, 10) || 0);
        }, sig);
        // Tiles are <div role="button"> (a <button> can't wrap the toggle button).
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            this.open(parseInt(btn.dataset.index, 10) || 0);
          }
        }, sig);
        // Start fetching the big image on intent, so opening feels instant.
        const warm = () => this.preloadSlide(parseInt(btn.dataset.index, 10) || 0);
        btn.addEventListener('pointerenter', warm, sig);
        btn.addEventListener('focus', warm, sig);
        btn.addEventListener('touchstart', warm, { signal: this.ac.signal, passive: true });
      });

      if (this.box) {
        const q = (s) => this.box.querySelector(s);
        const ov = q('.stl__overlay');
        const cl = q('.stl__close');
        const pv = q('.stl__nav--prev');
        const nx = q('.stl__nav--next');
        if (ov) ov.addEventListener('click', () => this.close(), sig);
        if (cl) cl.addEventListener('click', () => this.close(), sig);
        if (pv) pv.addEventListener('click', () => this.go(-1), sig);
        if (nx) nx.addEventListener('click', () => this.go(1), sig);
        document.addEventListener('keydown', (e) => this.onKey(e), sig);
        // Safari's bars slide in/out and rotation changes the box → re-measure.
        window.addEventListener('resize', () => this.syncVh(), sig);
        window.addEventListener('orientationchange', () => this.syncVh(), sig);
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', () => this.syncVh(), sig);
        }
        // "More inspiration" thumbnails (mobile) switch to that look.
        this.moreThumbs.forEach((btn) => {
          btn.addEventListener('click', () => this.open(parseInt(btn.dataset.index, 10) || 0), sig);
        });
      }

      if (this.moreBtn) this.moreBtn.addEventListener('click', () => this.reveal(), sig);

      if (this.grid && 'ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.sizeRows());
        this.ro.observe(this.grid);
      }

      this.sizeRows();
      this.applyVisibility();
      this.setupVideoObserver();
    }

    disconnectedCallback() {
      if (this.ac) this.ac.abort();
      if (this.ro) this.ro.disconnect();
      if (this.vio) this.vio.disconnect();
      if (this.isOpen) document.body.classList.remove('stl-open');
      if (this.box && this.box.parentElement === document.body) this.box.remove();
    }

    /* Exact masonry row height: R = colW * 4/3, so a span-2 tile = 2R + gap. */
    sizeRows() {
      if (!this.grid) return;
      const cs = getComputedStyle(this.grid);
      let cols = parseInt(cs.getPropertyValue('--cols'), 10);
      if (!cols || cols < 1) {
        const tpl = cs.gridTemplateColumns;
        cols = tpl ? tpl.split(' ').filter(Boolean).length : 1;
      }
      let gap = parseFloat(cs.columnGap);
      if (isNaN(gap)) gap = 0;
      const w = this.grid.clientWidth;
      if (!w || !cols) return;
      const colW = (w - (cols - 1) * gap) / cols;
      const val = (colW * 4 / 3).toFixed(2) + 'px';
      if (this.grid.style.getPropertyValue('--stl-row') !== val) {
        this.grid.style.setProperty('--stl-row', val);
      }
    }

    /* Mobile full-bleed height: the media is "overlay height minus a 76px peek".
       That peek MUST be measured against the box the lightbox really occupies —
       100dvh is not it. iOS Safari resolves dvh against a viewport that can be
       shorter than the fixed overlay (address-bar state), so the media came out
       too short and half the panel showed on open (Kathrin 15.8., iPhone). The
       CSS keeps 100svh/100dvh as its fallback for the no-JS case. */
    syncVh() {
      if (!this.box || this.box.hidden) return;
      const h = Math.round(this.box.getBoundingClientRect().height);
      if (!h) return;
      const val = h + 'px';
      if (this.box.style.getPropertyValue('--stl-vh') !== val) {
        this.box.style.setProperty('--stl-vh', val);
      }
    }

    open(i) {
      if (!this.box || !this.slides.length) return;
      this.opener = document.activeElement;
      this.index = Math.max(0, Math.min(i, this.slides.length - 1));
      this.render();
      this.box.hidden = false;
      this.isOpen = true;
      document.body.classList.add('stl-open');
      // Only now does the overlay have a layout box: measure its real height and
      // redo the scroll reset (scrollTop is a no-op while display:none).
      this.syncVh();
      if (this.dialog) this.dialog.scrollTop = 0;
      const cl = this.box.querySelector('.stl__close');
      if (cl) cl.focus();
    }

    close() {
      if (!this.box) return;
      this.box.hidden = true;
      this.isOpen = false;
      document.body.classList.remove('stl-open');
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
    }

    go(d) {
      if (!this.slides.length) return;
      const n = this.slides.length;
      this.index = (this.index + d + n) % n;   // wrap-around across ALL looks
      this.render();
    }

    render() {
      this.slides.forEach((s, i) => {
        const active = i === this.index;
        s.classList.toggle('is-active', active);
        if (active) this.mountVideo(s.querySelector('.stl__slide-media[data-video-src]'));
      });
      this.preloadSlide(this.index);
      // Warm the neighbours so prev/next are instant too.
      const n = this.slides.length;
      if (n > 1) {
        this.preloadSlide((this.index + 1) % n);
        this.preloadSlide((this.index - 1 + n) % n);
      }
      // Hide the current look in the "More inspiration" grid; reset scroll to the top.
      this.moreThumbs.forEach((t) => t.classList.toggle('is-current', +t.dataset.index === this.index));
      if (this.dialog) this.dialog.scrollTop = 0;
    }

    /* Flip a slide's lazy image to eager so the fetch starts BEFORE the click.
       The slides sit in a display:none lightbox, where loading="lazy" never fires —
       that is why opening used to wait for the whole download. */
    preloadSlide(i) {
      const s = this.slides[i];
      if (!s) return;
      const img = s.querySelector('img[loading="lazy"]');
      if (img) img.loading = 'eager';
    }

    /* Defer every tile video until its cell nears the viewport. Mounting them all
       up front makes each autoplaying <video> stream at once, which starves the
       image downloads on look-heavy pages (26 tiles / 7 videos on community-looks).
       Hidden cells are observed too: display:none never intersects, so a cell
       revealed by "load more" fires on its own — no extra wiring needed. */
    setupVideoObserver() {
      const mediaOf = (btn) => btn.querySelector('.stl__media[data-video-src]');

      if (!('IntersectionObserver' in window)) {
        // Old browsers keep the previous behaviour rather than losing the videos.
        this.thumbs.forEach((btn) => {
          const cell = btn.closest('.stl__cell');
          if (cell && !cell.hidden) this.mountVideo(mediaOf(btn));
        });
        return;
      }

      this.vio = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const btn = entry.target.querySelector('.stl__thumb');
          if (btn) this.mountVideo(mediaOf(btn));
          obs.unobserve(entry.target);
        });
      }, { rootMargin: '300px 0px', threshold: 0 });

      this.thumbs.forEach((btn) => {
        if (!mediaOf(btn)) return;
        const cell = btn.closest('.stl__cell');
        if (cell) this.vio.observe(cell);
      });
    }

    /* Mount a <video> only once its tile/slide is visible, so global-video.js
       (which only adds its play/pause circle to VISIBLE videos, once) enhances
       it correctly. Its MutationObserver handles autoplay + the .gv-toggle.
       Sources (7.10.26): the Liquid only gets the 1080p mp4, the lighter renditions
       of the same file come as data-video-src-480 / -720. A tile (has -480) plays
       480p on phones, otherwise 720p; a lightbox slide (only -720) plays 720p on
       phones, otherwise 1080p. data-video-src (1080p) is always the LAST source:
       the browser takes the first <source> whose media matches, and a rendition
       that fails to load falls through to the next one. Read via getAttribute:
       dataset keeps the hyphen before a digit ("videoSrc-480").
       preload="none": autoplay starts the download anyway, and mounting already
       waits until the tile is near the viewport (setupVideoObserver). */
    mountVideo(media) {
      if (!media || !media.dataset.videoSrc || media.querySelector('video')) return;
      const v = document.createElement('video');
      v.muted = true;
      ['loop', 'muted', 'playsinline', 'webkit-playsinline', 'autoplay'].forEach((a) => v.setAttribute(a, ''));
      v.setAttribute('preload', 'none');
      if (media.dataset.poster) v.setAttribute('poster', media.dataset.poster);
      const addSource = (url, mq) => {
        const src = document.createElement('source');
        if (mq) src.setAttribute('media', mq);
        src.src = url;
        src.type = 'video/mp4';
        v.appendChild(src);
      };
      const phone = '(max-width: 749px)';
      const src480 = media.getAttribute('data-video-src-480');
      const src720 = media.getAttribute('data-video-src-720');
      if (src480) {
        addSource(src480, phone);
        if (src720) addSource(src720);
      } else if (src720) {
        addSource(src720, phone);
      }
      addSource(media.dataset.videoSrc);
      media.appendChild(v);
    }

    onKey(e) {
      if (!this.isOpen) return;
      // A quick-view drawer opened on top of us owns the keyboard, otherwise one
      // Escape would close both it and this lightbox at the same time.
      if (document.querySelector('quick-view-modal[open]')) return;
      if (e.key === 'Escape') {
        this.close();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.go(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.go(1);
      }
    }

    /* Chunked load-more: reveal `step` more cells per click, hide the button at the end. */
    applyVisibility() {
      const total = this.thumbs.length;
      this.thumbs.forEach((btn, i) => {
        const cell = btn.closest('.stl__cell');
        if (!cell) return;
        cell.hidden = i >= this.visible;
      });
      if (this.moreWrap) this.moreWrap.hidden = this.visible >= total;
      this.sizeRows();
    }

    reveal() {
      this.visible = Math.min(this.visible + this.step, this.thumbs.length);
      this.applyVisibility();
    }
  }

  if (!customElements.get('shop-the-look')) {
    customElements.define('shop-the-look', ShopTheLook);
  }
})();
